import React, { ReactElement, createContext, useContext, useMemo } from 'react';

import { BASE_PATH_2_QUERY, BASE_PATH_3_QUERY } from '@glass/common/modules/pages/queryParams';
import { Section } from '@glass/web/modules/resumes/router/sections';
import { Step } from '@glass/web/modules/resumes/router/steps';
import useBuilderStepParams from '@glass/web/modules/resumes/router/useBuilderStepParams';

import usePrefetchNextBuilderStep from '@glass/shared/components/ResumeBuilder/usePrefetchNextBuilderStep';
import useActiveResume from '@glass/shared/modules/resumes/hooks/useActiveResume';
import useNextStepParams from '@glass/shared/modules/resumes/hooks/useNextStepParams';

type Resume = {
  id: string;
};

const ResumeBuilderStepContext = createContext({
  currentStepParams: {},
  nextStepParams: {},
});

export const useResumeBuilderSteps = () => useContext(ResumeBuilderStepContext);

export default function ResumeBuilderStepProvider({
  resume,
  section,
  step,
  children,
}: {
  resume: Resume;
  section: Section;
  step: Step;
  children: ReactElement;
}) {
  // todo: add misc next step here
  const currentStepParams = useBuilderStepParams({
    [BASE_PATH_2_QUERY]: section,
    [BASE_PATH_3_QUERY]: step,
  });

  const isBuilderPage = !!currentStepParams[BASE_PATH_2_QUERY];
  const nextStepParams = useNextStepParams(currentStepParams, resume);

  usePrefetchNextBuilderStep(nextStepParams);
  useActiveResume({ resume, skip: !isBuilderPage });

  const value = useMemo(() => {
    return {
      currentStepParams,
      nextStepParams,
      isBuilderPage,
    };
  }, [currentStepParams, nextStepParams, isBuilderPage]);

  return (
    <ResumeBuilderStepContext.Provider value={value}>{children}</ResumeBuilderStepContext.Provider>
  );
}
